import { cord, cord3, formatInput, gql2 } from "@/gql/lib";
import { Subject, InvEntityType } from "../data/model";
import { fixTier3XY, correctTier12XY } from "@/gql/cords";
import { POPULATION_TYPE, PopulationTypeHelper } from "@/common/models";
import { SharedState } from "@/common/state";
import { UseModal } from "@/components/root/modal";
import FilesModalVue from "@/components/modals/files-modal/FilesModal.vue";
import {
  AxisType,
  ActionEnum,
  TimeAxisRangeInput,
  CoordinateInputTier3,
} from "@/gql/types";
import {
  FilesModalProps,
  FilterType,
} from "@/components/modals/files-modal/model";
import { TimeFrameOption } from "@/common/state/models";

const summary_sentence = (subject: Subject) => {
  switch (subject.type) {
    case POPULATION_TYPE.EMPLOYEE:
      return `Files accessible by ${subject.name}`;
    case POPULATION_TYPE.ORGANIZATION:
    case POPULATION_TYPE.PRIVATE_EMAIL:
    case POPULATION_TYPE.CONTACT:
      return `Files that were sent to ${subject.name}`;
    case POPULATION_TYPE.FOLDER:
      return `Files contained in ${subject.name} folder`;
    case POPULATION_TYPE.LABEL:
      return `${subject.name} files`;
    default:
      return "?";
  }
};

const get_summary = gql2<
  { subject: Subject; relative_time_frame: TimeFrameOption },
  { count: number; relative_count: number; type: InvEntityType; desc: string }
>("inv-counters.files", ({ subject, relative_time_frame }, { frame }) => {
  const cords = (time_frame: TimeAxisRangeInput) =>
    correctTier12XY({
      coordinate: {
        x: cord(AxisType.None),
        y: cord(PopulationTypeHelper.asAxisType2(subject.type, "both"), [
          subject.id,
        ]),
        z: time_frame,
      },
      action: ActionEnum.List,
    });
  return {
    query: `{
     current: tier1count(params:${formatInput(cords(frame))}){
        files
        uniqs
     }
     relative: tier1count(params:${formatInput(
       cords({
         type: relative_time_frame.type,
         values: [relative_time_frame.from],
       })
     )}){
      files
      uniqs
   }
    }`,
    mapper: (res) => {
      const { current, relative } = res;
      return {
        type: POPULATION_TYPE.FILE,
        count: current.length > 0 ? current[0].files : 0,
        relative_count: relative.length > 0 ? relative[0].files : 0,
        desc: summary_sentence(subject),
      };
    },
  };
});

const get_title = (subject: Subject, extra_props?: { label?: string }) => {
  const prefix =
    extra_props && extra_props.label
      ? `<b>${extra_props.label.replace(/information/i, "")}</b> `
      : "";
  switch (subject.type) {
    case POPULATION_TYPE.EMPLOYEE:
      return `${prefix}Files accessible by <b>${subject.name}</b>`;
    case POPULATION_TYPE.LABEL:
      return prefix
        ? `${prefix} information Files`
        : `<b>${subject.name.replace(
            /information/i,
            ""
          )}</b> information Files`;
    case POPULATION_TYPE.ORGANIZATION:
    case POPULATION_TYPE.PRIVATE_EMAIL:
    case POPULATION_TYPE.CONTACT:
      return `${prefix}Files that were sent to <b>${subject.name}</b>`;
    case POPULATION_TYPE.FOLDER:
    default:
      return `${prefix}Files contained in <b>${subject.name}</b>`;
  }
};

const get_main_cords = (subject: Subject) => {
  const { from, type } = SharedState.selectedTimeFrameOption.value;
  return fixTier3XY({
    x: cord3(AxisType.None),
    y: cord3(PopulationTypeHelper.asAxisType2(subject.type, "both"), [
      subject.id,
    ]),
    z: {
      type,
      values: [from],
    },
  });
};

const show_modal = async (
  subject: Subject,
  alt_coordinates: CoordinateInputTier3[] = [],
  extra_props?: {
    label?: string;
    hide_filters?: FilterType[];
  }
) => {
  const props: FilesModalProps = {
    coordinates:
      alt_coordinates.length > 0 ? alt_coordinates : [get_main_cords(subject)],
    files_title: get_title(subject, extra_props),
    hide_filters:
      extra_props && extra_props.hide_filters ? extra_props.hide_filters : [],
    show_back: false,
    files_only: true,
  };
  UseModal().show(FilesModalVue, props);
};

export default { get_summary, show_modal };
